import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { renderLinkedText } from "@/lib/content";
import { scrollTo } from "@/components/Navbar";

export const Packages = () => {
  const { content } = useContent();
  const pkg = content.packages;
  return (
  <section
    id="packages"
    className="bg-[#2C4C3B] py-28 lg:py-40"
    data-testid="packages-section"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="mb-20 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between"
      >
        <div className="max-w-2xl">
          <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#C5A059]">
            {pkg.kicker}
          </p>
          <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#FAF8F5] sm:text-5xl">
            {pkg.title_line1}{" "}
            <span className="italic text-[#C5A059]">{pkg.title_line2}</span>
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-[#FAF8F5]/70">
          {pkg.note}
        </p>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-3">
        {pkg.plans.map((p, i) => (
          <motion.article
            key={p.id}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            className={`relative flex flex-col rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-2 lg:p-10 ${
              p.featured
                ? "bg-[#FAF8F5] shadow-[0_40px_80px_rgba(0,0,0,0.3)] lg:-mt-6 lg:mb-6"
                : "border border-[#FAF8F5]/15 bg-[#FAF8F5]/5 backdrop-blur-sm"
            }`}
            data-testid={`package-card-${p.id}`}
          >
            {p.featured && (
              <span
                className="absolute -top-4 left-8 rounded-full bg-[#7A2B21] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#FAF8F5]"
                data-testid="package-featured-badge"
              >
                Most Chosen
              </span>
            )}
            <h3
              className={`font-display text-2xl font-medium tracking-tight ${
                p.featured ? "text-[#1C1917]" : "text-[#FAF8F5]"
              }`}
            >
              {renderLinkedText(p.name)}
            </h3>
            <p
              className={`mt-2 text-xs leading-relaxed ${
                p.featured ? "text-[#57534E]" : "text-[#FAF8F5]/65"
              }`}
            >
              {p.tagline}
            </p>
            <div className="mt-6 flex items-baseline gap-1">
              <span
                className={`font-display text-5xl font-medium tracking-tight ${
                  p.featured ? "text-[#7A2B21]" : "text-[#C5A059]"
                }`}
                data-testid={`package-price-${p.id}`}
              >
                {p.price}
              </span>
              <span
                className={`text-sm ${
                  p.featured ? "text-[#57534E]" : "text-[#FAF8F5]/60"
                }`}
              >
                {p.period}
              </span>
            </div>
            <ul className="mt-8 flex-1 space-y-4">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                      p.featured
                        ? "bg-[#7A2B21]/10 text-[#7A2B21]"
                        : "bg-[#C5A059]/15 text-[#C5A059]"
                    }`}
                  >
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span
                    className={`text-sm leading-relaxed ${
                      p.featured ? "text-[#1C1917]" : "text-[#FAF8F5]/85"
                    }`}
                  >
                    {renderLinkedText(f)}
                  </span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => scrollTo("#enquire")}
              data-testid={`package-cta-${p.id}`}
              className={`mt-10 rounded-full py-4 text-sm font-bold uppercase tracking-wider transition-[background-color,transform] duration-300 hover:scale-[1.02] active:scale-[0.98] ${
                p.featured
                  ? "bg-[#7A2B21] text-[#FAF8F5] hover:bg-[#5C1E16]"
                  : "border border-[#FAF8F5]/30 text-[#FAF8F5] hover:bg-[#FAF8F5]/10"
              }`}
            >
              Start with {p.name}
            </button>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
  );
};
